import { useState } from 'react'
import SearchBar from '../components/SearchBar'
import ResultsPanel from '../components/ResultsPanel'
import Header from '../components/Header'
import ReportPage from './ReportPage'
import WelcomeDashboard from '../components/WelcomeDashboard'
import '../pages/Dashboard.css'

const PYTHON_API = ''

// ─── Datos geográficos completos por país ──────────────────────────────────────
const COUNTRY_CONFIG = {
  US: {
    name: 'United States', currency: 'USD', symbol: '$',
    cities: [
      { name: 'New York',     interest: 92 }, { name: 'Los Angeles',  interest: 86 },
      { name: 'Chicago',      interest: 79 }, { name: 'Houston',      interest: 73 },
      { name: 'Miami',        interest: 68 }, { name: 'Atlanta',      interest: 61 },
      { name: 'Dallas',       interest: 57 }, { name: 'Seattle',      interest: 54 },
    ],
    mapsCity: 'New York, NY',
    amazon: { domain: 'amazon.com', bsr_scale: 1 },
    fb_market: 'United States',
    trend_base: { avg: 71, peak: 95, direction: 'rising', change_pct: 22.1 },
    price_mult: 1.0,
  },
  CA: {
    name: 'Canada', currency: 'CAD', symbol: 'CA$',
    cities: [
      { name: 'Toronto',    interest: 82 }, { name: 'Vancouver',  interest: 74 },
      { name: 'Montreal',   interest: 65 }, { name: 'Calgary',    interest: 58 },
      { name: 'Ottawa',     interest: 51 }, { name: 'Edmonton',   interest: 47 },
    ],
    mapsCity: 'Toronto, ON',
    amazon: { domain: 'amazon.ca', bsr_scale: 0.12 },
    fb_market: 'Canada',
    trend_base: { avg: 58, peak: 82, direction: 'rising', change_pct: 15.3 },
    price_mult: 1.35,
  },
  GB: {
    name: 'United Kingdom', currency: 'GBP', symbol: '£',
    cities: [
      { name: 'London',       interest: 88 }, { name: 'Manchester',   interest: 72 },
      { name: 'Birmingham',   interest: 65 }, { name: 'Edinburgh',    interest: 59 },
      { name: 'Bristol',      interest: 54 }, { name: 'Leeds',        interest: 48 },
    ],
    mapsCity: 'London',
    amazon: { domain: 'amazon.co.uk', bsr_scale: 0.18 },
    fb_market: 'United Kingdom',
    trend_base: { avg: 62, peak: 85, direction: 'stable', change_pct: 8.4 },
    price_mult: 0.82,
  },
  AU: {
    name: 'Australia', currency: 'AUD', symbol: 'AU$',
    cities: [
      { name: 'Sydney',      interest: 85 }, { name: 'Melbourne',  interest: 78 },
      { name: 'Brisbane',    interest: 64 }, { name: 'Perth',      interest: 57 },
      { name: 'Adelaide',    interest: 48 }, { name: 'Canberra',   interest: 41 },
    ],
    mapsCity: 'Sydney, NSW',
    amazon: { domain: 'amazon.com.au', bsr_scale: 0.09 },
    fb_market: 'Australia',
    trend_base: { avg: 55, peak: 79, direction: 'rising', change_pct: 18.7 },
    price_mult: 1.55,
  },
  ES: {
    name: 'España', currency: 'EUR', symbol: '€',
    cities: [
      { name: 'Madrid',      interest: 91 }, { name: 'Barcelona',  interest: 84 },
      { name: 'Valencia',    interest: 67 }, { name: 'Sevilla',    interest: 59 },
      { name: 'Bilbao',      interest: 52 }, { name: 'Málaga',     interest: 46 },
      { name: 'Zaragoza',    interest: 40 }, { name: 'Murcia',     interest: 35 },
    ],
    mapsCity: 'Madrid',
    amazon: { domain: 'amazon.es', bsr_scale: 0.11 },
    fb_market: 'Spain',
    trend_base: { avg: 64, peak: 88, direction: 'rising', change_pct: 24.6 },
    price_mult: 0.92,
  },
  NZ: {
    name: 'New Zealand', currency: 'NZD', symbol: 'NZ$',
    cities: [
      { name: 'Auckland',      interest: 86 }, { name: 'Wellington',  interest: 71 },
      { name: 'Christchurch',  interest: 62 }, { name: 'Hamilton',    interest: 54 },
      { name: 'Dunedin',       interest: 44 },
    ],
    mapsCity: 'Auckland',
    amazon: { domain: 'amazon.com', bsr_scale: 0.04 },
    fb_market: 'New Zealand',
    trend_base: { avg: 49, peak: 72, direction: 'stable', change_pct: 6.2 },
    price_mult: 1.65,
  },
}

// ─── Google Trends real (Python server) ───────────────────────────────────────
async function fetchGoogleTrendsReal(keyword, countries) {
  try {
    const res = await fetch(`/api/py/api/trends/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keyword, countries, timeframe: '12m' }),
      signal: AbortSignal.timeout(30000),
    })
    if (!res.ok) return null
    return await res.json()
  } catch { return null }
}

// ─── Amazon real (RapidAPI via Python server) ────────────────────────────────
async function fetchAmazonReal(keyword, countries) {
  try {
    const res = await fetch('/api/py/api/amazon/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keyword, countries: countries.slice(0, 3) }),
      signal: AbortSignal.timeout(15000),
    })
    if (!res.ok) return null
    const data = await res.json()
    if (data.source === 'no_key' || !data.results) return null
    return data
  } catch { return null }
}

// ─── Keywords real (Google Ads / DataForSEO via Python server) ───────────────
async function fetchKeywordsReal(keyword, country) {
  try {
    const res = await fetch('/api/py/api/keywords/research', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keyword, country }),
      signal: AbortSignal.timeout(20000),
    })
    if (!res.ok) return null
    const data = await res.json()
    if (data.source === 'not_configured' || !data.keywords) return null
    return data
  } catch { return null }
}

// ─── Facebook Ads real (API oficial de Meta u opción B RapidAPI, vía Python) ──
async function fetchFacebookReal(keyword, countries) {
  try {
    const res = await fetch('/api/py/api/facebook/ads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keyword, countries }),
      signal: AbortSignal.timeout(60000),
    })
    if (!res.ok) return null
    const data = await res.json()
    if (data.source === 'no_key' || !data.ads) return null
    return data
  } catch { return null }
}

// ─── Pinterest real (RapidAPI via Python server) ──────────────────────────────
async function fetchPinterestReal(keyword) {
  try {
    const res = await fetch('/api/py/api/pinterest/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keyword, limit: 20 }),
      signal: AbortSignal.timeout(15000),
    })
    if (!res.ok) return null
    const data = await res.json()
    if (!data.pins || data.pins.length === 0) return null
    return data
  } catch { return null }
}

// ─── Combina competidores de Amazon (productos) y Facebook Ads (anunciantes) ───
function buildCompetitors(amazonData, facebookData) {
  const competitors = []

  // Fuente 1: Amazon
  if (amazonData?.results) {
    const products = Object.values(amazonData.results).find(arr => arr.length > 0) || []
    products.slice(0, 3).forEach(p => {
      const rating = parseFloat(p.rating) || 0
      const reviews = p.review_count || 0
      const strength = Math.round((rating / 5) * 60 + Math.min(reviews / 5000, 1) * 40)
      const raw = (p.title || '').replace(/&amp;/g, '&').replace(/&quot;/g, '"')
      const title = raw.length > 70 ? raw.slice(0, 70) + '…' : raw
      competitors.push({ name: title, price: p.price || '—', rating: rating || null, reviews, url: p.url || null, domain: p.domain || 'amazon.com', strength, source: 'amazon' })
    })
  }

  // Fuente 2: Facebook Ads (anunciantes activos = competidores en publicidad)
  if (facebookData?.ads?.length > 0) {
    const seen = new Set()
    for (const ad of facebookData.ads) {
      const pageName = (ad.page_name || '').trim()
      if (!pageName || seen.has(pageName)) continue
      seen.add(pageName)
      if (competitors.length >= 6) break
      const strength = ad.is_active ? (ad.spend_upper ? 75 : 55) : 40
      competitors.push({
        name: pageName, price: null, rating: null, reviews: null,
        url: ad.snapshot_url || null, domain: 'facebook.com',
        strength, source: 'facebook',
        body: ad.body ? String(ad.body).slice(0, 100) : '',
      })
    }
  }

  return competitors
}

// ─── Componente principal ──────────────────────────────────────────────────────
function Dashboard({ onNavigate }) {
  const [results, setResults] = useState(null)
  const [loading, setLoading] = useState(false)
  const [analysisType, setAnalysisType] = useState(null)
  const [selectedCountries, setSelectedCountries] = useState([])
  const [showReport, setShowReport] = useState(false)

  const handleSearch = async (query, selectedCountries = ['US', 'CA', 'GB', 'AU', 'ES', 'NZ']) => {
    if (!selectedCountries.length) selectedCountries = ['US', 'CA', 'GB', 'AU', 'ES', 'NZ']
    setLoading(true)
    try {
      // Verificar estado de APIs en paralelo con Google Trends
      const [realTrends, realKeywords, realPinterest, healthData, realAmazon, realFacebook] = await Promise.all([
        fetchGoogleTrendsReal(query, selectedCountries),
        fetchKeywordsReal(query, selectedCountries[0] || 'US'),
        fetchPinterestReal(query),
        fetch('/api/health').then(r => r.json()).catch(() => ({ apis: {} })),
        fetchAmazonReal(query, selectedCountries),
        fetchFacebookReal(query, selectedCountries),
      ])
      const apiStatus = healthData?.apis || {}
      const aliexpressOk = apiStatus.aliexpress?.status === 'ok'
      const amazonOk = apiStatus.amazon?.status === 'ok'
      const facebookOk = apiStatus.facebook?.status === 'ok' || apiStatus.facebook_rapidapi?.status === 'ok'

      // ── Cálculo real de scores desde datos de APIs ──────────────────────────────────────
      const amazonProducts = realAmazon?.results
        ? Object.values(realAmazon.results).flat()
        : []
      const amazonCount = amazonProducts.length

      const parsePrice = s => parseFloat(String(s || '0').replace(/[$,£€CA]/g, '').trim()) || 0
      const parseNum   = s => parseInt(String(s || '0').replace(/,/g, '').trim()) || 0

      const prices   = amazonProducts.map(p => parsePrice(p.price || p.product_price)).filter(v => v > 0)
      const ratings  = amazonProducts.map(p => parseFloat(p.rating || p.product_star_rating || 0)).filter(v => v > 0)
      const reviews  = amazonProducts.map(p => parseNum(p.review_count || p.product_num_ratings)).filter(v => v > 0)
      const avgPrice  = prices.length  ? prices.reduce((a, b) => a + b, 0)  / prices.length  : 0
      const avgRating = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0
      const avgReviews= reviews.length ? reviews.reduce((a, b) => a + b, 0) / reviews.length : 0

      const kwList = realKeywords?.keywords || []
      const kwWithVol = kwList.filter(k => k.volume > 0)
      const avgVolume = kwWithVol.length ? kwWithVol.reduce((a, k) => a + k.volume, 0) / kwWithVol.length : 0
      const avgCPC    = kwList.length ? kwList.reduce((a, k) => a + (k.cpc || 0), 0) / kwList.length : 0
      const highComp  = kwList.filter(k => k.competition === 'HIGH').length / Math.max(kwList.length, 1)

      const trendCountry = realTrends?.countries
        ? Object.values(realTrends.countries)[0]
        : null
      const trendRaw = realTrends?.score
        ?? realTrends?.trendScore
        ?? realTrends?.global_trend?.avg
        ?? trendCountry?.avg
        ?? null
      const trendDirection = realTrends?.trend_direction || trendCountry?.direction || 'stable'
      const trendSeries = realTrends?.global_trend?.series || trendCountry?.series || []

      // DEMAND: keyword volume + Amazon market presence
      let demand = null
      if (avgVolume > 0 || amazonCount > 0) {
        const volScore = avgVolume > 0
          ? Math.min(100, Math.round(Math.log10(avgVolume + 1) / Math.log10(100001) * 100))
          : 35
        const azScore = Math.min(100, amazonCount * 8)
        demand = Math.max(10, Math.round(volScore * 0.65 + azScore * 0.35))
      } else if (trendRaw !== null) {
        demand = Math.max(10, Math.round(trendRaw * 0.7))
      }

      // TREND: directly from Google Trends real score
      const trend = trendRaw !== null ? Math.max(5, Math.round(trendRaw)) : null

      // COMPETITION: low keyword competition + fewer Amazon competitors = better score
      let competition = null
      if (kwList.length > 0 || amazonCount > 0) {
        const kwComp = kwList.length > 0 ? Math.round((1 - highComp) * 65) : 50
        const azComp = Math.max(10, Math.min(35, 35 - Math.round(amazonCount * 2.5)))
        competition = Math.max(5, Math.min(95, kwComp + azComp))
      }

      // MARGIN: from Amazon average price (higher price = better dropshipping margin)
      let margin = null
      if (avgPrice > 0) {
        margin = Math.min(95, Math.max(15, Math.round(
          Math.log10(Math.max(avgPrice, 1) + 1) / Math.log10(1001) * 100
        )))
      }

      // AUDIENCE: blend demand + trend
      let audience = null
      if (demand !== null && trend !== null) {
        audience = Math.round(demand * 0.55 + trend * 0.45)
      } else if (demand !== null) {
        audience = Math.round(demand * 0.85)
      } else if (trend !== null) {
        audience = Math.round(trend * 0.7)
      }

      // OVERALL SCORE: weighted from real components
      const compMap = { demand: 0.30, trend: 0.25, competition: 0.20, margin: 0.15, audience: 0.10 }
      const compVals = { demand, trend, competition, margin, audience }
      let wSum = 0, wTot = 0
      Object.entries(compMap).forEach(([k, w]) => {
        if (compVals[k] !== null) { wSum += compVals[k] * w; wTot += w }
      })
      const score = wTot > 0 ? Math.round(wSum / wTot) : null

      const status = !score ? 'ANALYZING'
        : score >= 75 ? 'VIABLE'
        : score >= 50 ? 'MODERATE'
        : 'RISKY'

      const recommendation = !score
        ? 'Analizando datos de mercado...'
        : score >= 80 ? 'Proceder inmediatamente — Oportunidad de alta rentabilidad'
        : score >= 65 ? 'Mercado favorable — Inversión moderada recomendada'
        : score >= 50 ? 'Proceder con cautela — Competencia considerable'
        : 'Alto riesgo — Considera nichos alternativos'

      const realResults = {
        query,
        type: analysisType || 'PRODUCT',
        countries: selectedCountries,
        score: score ?? 0,
        status,
        recommendation,
        components: {
          demand:      demand      ?? 0,
          margin:      margin      ?? 0,
          trend:       trend       ?? 0,
          audience:    audience    ?? 0,
          competition: competition ?? 0,
        },
        competitors: buildCompetitors(amazonOk ? realAmazon : null, facebookOk && realFacebook ? realFacebook : null),
        keywords: kwList.length
          ? kwList.slice(0, 5).map(k => ({
              term: k.term, volume: k.volume, cpc: k.cpc,
              profitability: k.profitability, competition: k.competition,
            }))
          : [],
        keywordsSource: realKeywords?.source || 'not_configured',
        niches: [],
        googleTrends: realTrends || null,
        pinterestTrends: (() => {
          if (!realPinterest?.pins?.length) return { not_configured: true }
          const pins = realPinterest.pins
          const withSaves = pins.filter(p => (p.saves || 0) > 0)
          const totalSaves = pins.reduce((s, p) => s + (p.saves || 0), 0)
          const avgSaves = pins.length > 0 ? Math.round(totalSaves / pins.length) : 0
          const maxSaves = Math.max(...pins.map(p => p.saves || 0))
          // interest_score: basado en cantidad de pins (hasta 70 pts) + calidad de saves (hasta 30 pts)
          const countScore = Math.min(70, Math.round(pins.length / 25 * 70))
          const saveScore  = Math.min(30, Math.round(Math.log10(avgSaves + 1) / 4 * 30))
          const interest_score = countScore + saveScore
          // Chart: saves de los top 10 pins ordenados desc
          const topPins = [...pins].sort((a, b) => (b.saves || 0) - (a.saves || 0)).slice(0, 10)
          const savesChart = topPins.map((p, i) => ({
            name: String(i + 1),
            saves: p.saves || 0,
            title: (p.title || '').slice(0, 40),
          }))
          // Extraer términos relacionados de títulos
          const words = pins.flatMap(p => (p.title || '').toLowerCase().split(/[\s,\-–()]+/).filter(w => w.length > 3))
          const freq = {}
          words.forEach(w => { freq[w] = (freq[w] || 0) + 1 })
          const related = Object.entries(freq)
            .filter(([w]) => !query.toLowerCase().split(' ').includes(w))
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6)
            .map(([w]) => w)
          return {
            keyword: query,
            is_mock: false,
            source: 'pinterest-scraper-v2',
            total_pins: pins.length,
            pins_with_saves: withSaves.length,
            interest_score,
            avg_saves: avgSaves,
            max_saves: maxSaves,
            savesChart,
            related,
            trend_direction: interest_score >= 60 ? 'rising' : interest_score >= 35 ? 'stable' : 'falling',
          }
        })(),
        dropshipping: (analysisType || 'PRODUCT') === 'PRODUCT'
          ? (aliexpressOk ? null : { not_configured: true })
          : null,
        amazon: (analysisType || "PRODUCT") === "PRODUCT" && amazonOk && realAmazon
          ? realAmazon.results
          : null,
        facebookAds: facebookOk && realFacebook?.ads?.length > 0 ? realFacebook : null,
        googleMaps: null,
      }

      setResults(realResults)
      setShowReport(false)
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateReport = () => setShowReport(true)
  const handleBackFromReport  = () => setShowReport(false)

  if (showReport && results) {
    return <ReportPage results={results} onBack={handleBackFromReport} analysisType={analysisType} />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-bg via-slate-darker to-dark-bg">
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-slate-medium rounded-full blur-3xl opacity-10"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-slate-dark rounded-full blur-3xl opacity-10"></div>
      </div>

      <div className="relative z-10">
        <Header onNavigate={onNavigate} />

        <div className="max-w-7xl mx-auto px-4 py-4">
          <SearchBar
            onSearch={handleSearch}
            loading={loading}
            analysisType={analysisType}
            setAnalysisType={setAnalysisType}
            hasResults={!!results}
            onClear={() => setResults(null)}
            selectedCountries={selectedCountries}
            onCountriesChange={setSelectedCountries}
          />

          {results && (
            <div className="mt-8">
              <ResultsPanel
                results={results}
                onGenerateReport={handleGenerateReport}
              />
            </div>
          )}

          {!results && !loading && (
            <div className="mt-6">
              <WelcomeDashboard onNavigate={onNavigate} selectedCountries={selectedCountries} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Dashboard
